import { Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useProvisioning } from "../hooks/useProvisioning";
import { NavIcon } from "../app/NavIcons";
import css from "./GettingStartedPage.module.css";

/**
 * First-run guided setup (ADR 175). On a freshly-cleared system the app has no
 * tenant/site/warehouse, so instead of dropping the admin into a dead dashboard
 * we lead with an ordered checklist that deep-links each existing create flow.
 * Steps and their done/current state come from useProvisioning.
 *
 * The `other` branch is the third state (ADR 223): authenticated by the IdP but
 * holding no grant. It is not an error and not a checklist — it names the
 * situation and shows the account, because handing that string to an
 * administrator is the one action that resolves it.
 */

/** The signed-in account, read the same way the shell header reads it. */
function accountName(): string | undefined {
  return (window as unknown as { __auth?: { username?: string } }).__auth?.username;
}

export function GettingStartedPage() {
  const { t } = useTranslation();
  const { role, steps, complete, loading } = useProvisioning();
  const noAccess = !loading && role === "other";
  const account = accountName();

  // A granted account that can provision nothing has no business here — a stale
  // tab or a typed URL must not read as "no access" (ADR 408).
  if (!loading && role === "operator") return <Navigate to="/" replace />;

  return (
    <div className={css.page}>
      <header className={css.head}>
        <h1 className={css.title}>{noAccess ? t("setup.noAccess.title") : t("setup.title")}</h1>
        {!noAccess && <p className={css.subtitle}>{t("setup.subtitle")}</p>}
      </header>

      {loading ? (
        <div className={css.list} aria-busy="true">
          <div className={`${css.card} skeleton`} style={{ height: 96 }} />
          <div className={`${css.card} skeleton`} style={{ height: 96 }} />
        </div>
      ) : noAccess ? (
        // The heading already names the state; the account and what to do with it
        // are the only things left worth saying.
        <div className={css.notice}>
          {account && (
            <p className={css.account}>
              <span className={css.accountLabel}>{t("setup.noAccess.accountLabel")}</span>
              <code className={css.accountValue}>{account}</code>
            </p>
          )}
          <p className={css.noticeDesc}>{t("setup.noAccess.hint")}</p>
        </div>
      ) : complete ? (
        <div className={css.notice}>
          <h2 className={css.noticeTitle}>{t("setup.complete.title")}</h2>
          <p className={css.noticeDesc}>{t("setup.complete.desc")}</p>
          <div className={css.actions}>
            <Link to="/live" className={css.primary}>{t("setup.complete.live")}</Link>
            <Link to="/admin/warehouses" className={css.secondary}>{t("setup.complete.map")}</Link>
          </div>
        </div>
      ) : (
        <ol className={css.list}>
          {steps.map((s, i) => (
            <li
              key={s.key}
              className={s.done ? css.stepDone : s.current ? css.stepCurrent : css.stepUpcoming}
              aria-current={s.current ? "step" : undefined}
            >
              <div className={css.marker} aria-hidden="true">
                {s.done ? <NavIcon name="check" /> : i + 1}
              </div>
              <div className={css.body}>
                <div className={css.stepLabel}>{t("setup.stepLabel", { n: i + 1 })}</div>
                <h2 className={css.stepTitle}>{t(`setup.step.${s.key}.title`)}</h2>
                <p className={css.stepDesc}>{t(`setup.step.${s.key}.desc`)}</p>
                {s.done ? (
                  <span className={css.doneBadge}>{t("setup.done")}</span>
                ) : s.current ? (
                  <Link to={s.ctaTo} className={css.primary}>{t(`setup.step.${s.key}.cta`)}</Link>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
